/**
 * Helpers to recover when the app fails to load a JS chunk.
 *
 * Why this happens:
 * - We split the app into many small JS files ("chunks") and load each one only
 *   when needed (e.g. when you open the Findings page). The file names include a
 *   content hash, like `App-CbNQVy8T.js`.
 * - Each build lives in its own folder and deploys keep recent builds, so an
 *   open tab can still load its own build's files. A tab older than the kept
 *   builds (see prune-old-builds.sh), or a network that drops the request,
 *   makes the import fail with:
 *   `TypeError: Failed to fetch dynamically imported module`.
 *
 * The fix: fetch a fresh `index.html`, i.e. reload the page. These helpers
 * reload once (you land back on the same URL) and guard against reload loops.
 */

const RELOAD_GUARD_KEY = 'plerion:chunk-reload-at';

// After we reload once, ignore further chunk errors for this long. If a chunk is
// truly broken (not just stale), this stops the page reloading over and over.
const RELOAD_SUPPRESS_MS = 10_000;

/**
 * Returns true if the error looks like a failed dynamic `import()` of a chunk —
 * i.e. the file is missing (stale deploy) or the server returned something that
 * isn't valid JS. Different browsers word this error differently, so we check a
 * few known variants.
 */
export const isChunkLoadError = (error: unknown): boolean => {
  if (!error) {
    return false;
  }

  const name = error instanceof Error ? error.name : '';
  const message = error instanceof Error ? error.message : String(error);

  return (
    name === 'ChunkLoadError' ||
    /failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /importing a module script failed/i.test(message)
  );
};

/**
 * Reloads the page once to fetch a fresh `index.html` (and the new chunk file
 * names), landing the user back on the same URL but on the new build.
 *
 * Returns false without reloading if we already reloaded in the last
 * {@link RELOAD_SUPPRESS_MS}. That tells the caller "reloading didn't fix it"
 * so it can show a message instead of looping forever.
 */
export const reloadOnStaleChunk = (): boolean => {
  // When did we last reload to recover? Stored in sessionStorage so it survives
  // the reload but resets when the tab closes.
  let lastReloadAt = 0;
  try {
    lastReloadAt = Number(sessionStorage.getItem(RELOAD_GUARD_KEY)) || 0;
  } catch {
    // Some private/incognito modes block sessionStorage — treat as "never reloaded".
  }

  const now = Date.now();
  // We just reloaded and it still failed → don't reload again, let the caller
  // show the error screen.
  if (now - lastReloadAt < RELOAD_SUPPRESS_MS) {
    return false;
  }

  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(now));
  } catch {
    // If we can't record the timestamp, worst case is one extra reload — still safe.
  }

  window.location.reload();
  return true;
};
